import React, { useState, useRef, useCallback, type ReactNode } from 'react';
import BottomSheet from '../forms/BottomSheet';
import ButtonList from '../buttons/ButtonList';
import { useLongPress } from '../../hooks';
import config from '../../../config/config';
import './UserList.css';

export interface User {
  userId: string;
  userName: string;
  userRole?: string; // @deprecated - используйте roles вместо этого
  roles?: Array<{ id: number; roleName?: string; roleId: number; hierarchyLevel?: number }>; // Новое: массив ролей
  isCreator?: boolean;
  avatarColor?: string;
  avatarUrl?: string; // URL аватарки (для обратной совместимости, можно передать напрямую)
  currentAvatarId?: number; // ID активной аватарки (для новой архитектуры)
  onManagePermissions?: () => void;
  onManageRoles?: () => void; // Новое: управление ролями
  onSelectUser?: () => void; // Для режима выбора (используется в UserSelector)
  onViewProfile?: () => void; // Открыть профиль пользователя
  /** Настроить ставку по умолчанию на объекте (только при наличии права FINANCE_EDIT) */
  onConfigureDefaultRate?: () => void;
  /** Доп. блок справа (например, действия вне стандартного меню) */
  trailing?: ReactNode;
  /** Не открывать bottom sheet с действиями по long press */
  disableLongPress?: boolean;
  /** Присутствие у станции (маяк / брелок): зелёная точка и подпись под ролью */
  presenceOnline?: boolean;
  presenceLastSeenLabel?: string | null;
  presenceTitle?: string;
}

export interface UserListProps {
  users: User[];
  onAddUser?: () => void;
  onRemoveUser?: (userId: string) => void;
  canRemoveUser?: (userId: string) => boolean; // Функция проверки возможности удаления
  addButtonText?: string;
  addButtonSubtext?: string;
  showRemoveButton?: boolean;
  className?: string;
  canManagePermissions?: boolean; // есть ли право SITE_MANAGE_PERMISSION у текущего пользователя
  canManageMembers?: boolean; // есть ли право SITE_MANAGE_MEMBERS у текущего пользователя
  // Для множественного выбора
  multiSelect?: boolean;
  selectedUserIds?: string[]; // Массив ID выбранных пользователей
  onSelectionChange?: (selectedUserIds: string[]) => void; // Callback при изменении выбора
  /** Сохранить порядок `users` (не сортировать по иерархии ролей) */
  preserveOrder?: boolean;
}

// Вспомогательные функции
const getUserInitials = (name: string): string => {
  return name
    .split(' ')
    .map(word => word.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

const getAvatarColor = (userId: string): string => {
  const colors = [
    '#ef4444', '#f97316', '#f59e0b', '#eab308',
    '#84cc16', '#22c55e', '#10b981', '#06b6d4',
    '#0ea5e9', '#3b82f6', '#6366f1', '#8b5cf6',
    '#a855f7', '#d946ef', '#ec4899', '#f43f5e'
  ];
  const index = userId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return colors[index % colors.length];
};

// Компонент элемента списка пользователей
interface UserListItemProps {
  user: User;
  isSelected: boolean;
  multiSelect: boolean;
  onShortPress: () => void;
  onLongPress: () => void;
}

// В режиме выбора одного пользователя (напр. фильтр) увеличиваем порог движения,
// чтобы на мобильных лёгкое смещение пальца не считалось скроллом и не отменяло выбор
const MOVE_THRESHOLD_SELECT = 28;
const MOVE_THRESHOLD_DEFAULT = 10;

const UserListItem: React.FC<UserListItemProps> = ({
  user,
  isSelected,
  multiSelect,
  onShortPress,
  onLongPress
}) => {
  const isSelectionMode = !multiSelect && !!user.onSelectUser;
  const noRowMenu = multiSelect || user.disableLongPress;
  const longPressHandlers = useLongPress({
    onShortPress: multiSelect ? undefined : onShortPress,
    onLongPress: noRowMenu ? undefined : onLongPress,
    threshold: 500,
    moveThreshold: isSelectionMode ? MOVE_THRESHOLD_SELECT : MOVE_THRESHOLD_DEFAULT,
    vibrate: !multiSelect
  });

  // Для multiSelect режима создаем собственную логику с защитой от скролла
  const startPosRef = useRef<{ x: number; y: number } | null>(null);
  const isScrollingRef = useRef(false);
  const MOVE_THRESHOLD = isSelectionMode ? MOVE_THRESHOLD_SELECT : 10; // Порог движения в пикселях

  const handleMultiSelectStart = useCallback((clientX: number, clientY: number) => {
    startPosRef.current = { x: clientX, y: clientY };
    isScrollingRef.current = false;
  }, []);

  const handleMultiSelectMove = useCallback((clientX: number, clientY: number) => {
    if (!startPosRef.current) return;

    const deltaX = Math.abs(clientX - startPosRef.current.x);
    const deltaY = Math.abs(clientY - startPosRef.current.y);

    // Если движение превышает порог - это скролл
    if (deltaX > MOVE_THRESHOLD || deltaY > MOVE_THRESHOLD) {
      isScrollingRef.current = true;
    }
  }, []);

  const handleMultiSelectEnd = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    // Если это был скролл - игнорируем клик
    if (isScrollingRef.current) {
      isScrollingRef.current = false;
      startPosRef.current = null;
      return;
    }

    // Если не было начальной позиции - игнорируем
    if (!startPosRef.current) return;

    // Это был клик без движения - выполняем действие
    e.preventDefault();
    e.stopPropagation();
    startPosRef.current = null;
    onShortPress();
  }, [onShortPress]);

  // Обработчики для multiSelect режима
  const multiSelectHandlers = multiSelect ? {
    onMouseDown: (e: React.MouseEvent) => {
      handleMultiSelectStart(e.clientX, e.clientY);
    },
    onMouseMove: (e: React.MouseEvent) => {
      handleMultiSelectMove(e.clientX, e.clientY);
    },
    onMouseUp: (e: React.MouseEvent) => {
      handleMultiSelectEnd(e);
    },
    onMouseLeave: () => {
      startPosRef.current = null;
      isScrollingRef.current = false;
    },
    onTouchStart: (e: React.TouchEvent) => {
      const touch = e.touches[0];
      handleMultiSelectStart(touch.clientX, touch.clientY);
    },
    onTouchMove: (e: React.TouchEvent) => {
      const touch = e.touches[0];
      handleMultiSelectMove(touch.clientX, touch.clientY);
    },
    onTouchEnd: (e: React.TouchEvent) => {
      handleMultiSelectEnd(e);
    },
    onTouchCancel: () => {
      startPosRef.current = null;
      isScrollingRef.current = false;
    }
  } : {};

  // В режиме выбора одного пользователя (фильтр) используем только onClick — на мобильных
  // touch-события часто мешают скроллу или не доходят, а click срабатывает стабильно после тапа
  const selectionModeHandlers = isSelectionMode
    ? {
        onClick: (e: React.MouseEvent) => {
          e.preventDefault();
          e.stopPropagation();
          onShortPress();
        },
        role: 'button' as const,
        tabIndex: 0,
        onKeyDown: (e: React.KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onShortPress();
          }
        }
      }
    : {};

  const handlers = isSelectionMode
    ? selectionModeHandlers
    : multiSelect
      ? multiSelectHandlers
      : longPressHandlers.handlers;

  return (
    <div 
      className={`user-list-item ${multiSelect ? 'multi-select' : ''} ${isSelected ? 'selected' : ''} ${
        user.trailing ? 'user-list-item--with-trailing' : ''
      } ${user.disableLongPress ? 'user-list-item--static' : ''}`}
      {...handlers}
      style={{ 
        cursor: isSelectionMode || !user.disableLongPress ? 'pointer' : 'default',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        touchAction: isSelectionMode ? 'manipulation' : 'pan-y' // manipulation убирает задержку 300ms перед click на мобильных
      }}
    >
      <div className="user-avatar">
        {user.avatarUrl ? (
          <img 
            src={user.avatarUrl.startsWith('http') ? user.avatarUrl : `${config.api.baseURL.replace('/api/v1', '').replace('/api/v2', '')}${user.avatarUrl}`}
            alt={user.userName}
            className="avatar-image"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
              const fallback = target.nextElementSibling as HTMLElement;
              if (fallback) {
                fallback.style.display = 'flex';
                fallback.style.visibility = 'visible';
                fallback.style.opacity = '1';
              }
            }}
            onLoad={(e) => {
              const target = e.target as HTMLImageElement;
              const fallback = target.nextElementSibling as HTMLElement;
              if (fallback) {
                fallback.style.display = 'none';
              }
            }}
          />
        ) : null}
        <div 
          className="avatar-circle"
          style={{ 
            background: user.avatarColor || getAvatarColor(user.userId),
            display: user.avatarUrl ? 'none' : 'flex'
          }}
        >
          <span>{getUserInitials(user.userName)}</span>
        </div>
      </div>
      
      <div className="user-info">
        <div className="user-name">
          {user.userName}
          {user.isCreator && (
            <span className="creator-badge">Создатель</span>
          )}
        </div>
        {/* Отображение ролей */}
        {user.roles && user.roles.length > 0 ? (
          <div className="user-roles">
            {user.roles.map((role) => (
              <span key={role.id} className="role-badge">
                {role.roleName || `Роль ${role.roleId}`}
              </span>
            ))}
          </div>
        ) : (
          <div className="user-role">
            {user.userRole || 'Участник'}
          </div>
        )}
        {user.presenceOnline != null ? (
          <div className="user-presence-meta" title={user.presenceTitle}>
            <span
              className={`user-presence-dot${user.presenceOnline ? ' user-presence-dot--online' : ''}`}
              aria-hidden
            />
            <span className="user-presence-text">
              {user.presenceOnline ? 'В зоне' : 'Вне зоны'}
              {user.presenceLastSeenLabel ? ` · ${user.presenceLastSeenLabel}` : ''}
            </span>
          </div>
        ) : null}
      </div>
      {user.trailing ? <div className="user-list-item-trailing">{user.trailing}</div> : null}
    </div>
  );
};

// Функция для получения средней значимости участника на основе всех его ролей
// Складываем все hierarchyLevel и делим на количество ролей
const getAverageRoleLevel = (user: User): number => {
  if (!user.roles || user.roles.length === 0) {
    // Если ролей нет, возвращаем большое число, чтобы такие участники были в конце
    return 999;
  }
  
  // Получаем все hierarchyLevel ролей
  const levels = user.roles
    .map(r => r.hierarchyLevel)
    .filter((level): level is number => level !== undefined && level !== null);
  
  if (levels.length === 0) {
    // Если у всех ролей нет hierarchyLevel, возвращаем большое число
    return 999;
  }
  
  // Вычисляем среднее значение (сумма всех уровней / количество ролей)
  const sum = levels.reduce((acc, level) => acc + level, 0);
  return sum / levels.length;
};

const UserList: React.FC<UserListProps> = ({
  users,
  onAddUser,
  onRemoveUser,
  canRemoveUser,
  addButtonText = 'Добавить участника',
  addButtonSubtext = '',
  showRemoveButton = true,
  className = '',
  canManagePermissions = true,
  canManageMembers = true,
  multiSelect = false,
  selectedUserIds = [],
  onSelectionChange,
  preserveOrder = false
}) => {
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  
  // Сортируем участников по средней значимости ролей (от самого высокого к самому низкому)
  const sortedUsers = preserveOrder
    ? users
    : [...users].sort((a, b) => {
        const levelA = getAverageRoleLevel(a);
        const levelB = getAverageRoleLevel(b);
        return levelA - levelB; // Сортируем по возрастанию уровня (меньше = выше в иерархии)
      });

  // Обработчик множественного выбора
  const handleMultiSelectToggle = (userId: string) => {
    if (!onSelectionChange) return;
    
    const newSelection = selectedUserIds.includes(userId)
      ? selectedUserIds.filter(id => id !== userId)
      : [...selectedUserIds, userId];
    
    onSelectionChange(newSelection);
  };

  // Обработчик короткого нажатия
  const handleShortPress = (user: User) => {
    // Если включен множественный выбор
    if (multiSelect && onSelectionChange) {
      handleMultiSelectToggle(user.userId);
      return;
    }
    
    // Если есть onSelectUser (режим выбора), вызываем его
    if (user.onSelectUser) {
      user.onSelectUser();
      return;
    }
    
    // Иначе открываем профиль пользователя
    if (user.onViewProfile) {
      user.onViewProfile();
    }
  };

  // Обработчик долгого нажатия
  // В режиме multiSelect отключаем долгое нажатие, чтобы не мешать выделению
  const handleLongPress = (user: User) => {
    // Если включен множественный выбор, не показываем меню
    if (multiSelect) {
      return;
    }
    setSelectedUser(user);
    setShowUserMenu(true);
  };

  return (
    <div className={`user-list ${className}`}>
      {/* Кнопка добавления */}
      {onAddUser && (
        <div className="user-list-item add-user-item" onClick={onAddUser}>
          <div className="user-avatar">
            <div className="avatar-circle add-avatar">
              <i className="ri-add-line"></i>
            </div>
          </div>
          
          <div className="user-info">
            <div className="user-name">
              {addButtonText}
            </div>
            {addButtonSubtext ? (
              <div className="user-role">
                {addButtonSubtext}
              </div>
            ) : null}
          </div>
        </div>
      )}

      {/* Список пользователей */}
      {sortedUsers.map(user => (
        <UserListItem
          key={user.userId}
          user={user}
          isSelected={multiSelect && selectedUserIds.includes(user.userId)}
          multiSelect={multiSelect}
          onShortPress={() => handleShortPress(user)}
          onLongPress={multiSelect || user.disableLongPress ? () => {} : () => handleLongPress(user)}
        />
      ))}

      <BottomSheet
        isOpen={showUserMenu && !selectedUser?.onSelectUser}
        onClose={() => {
          setShowUserMenu(false);
          setSelectedUser(null);
        }}
        title={selectedUser?.userName || 'Действия'}
        size="small"
      >
        <ButtonList
          spacing="normal"
          showDividers={true}
          items={[
            {
              id: 'remove',
              icon: canManageMembers ? 'ri-user-unfollow-line' : 'ri-lock-line',
              label: 'Удалить участника',
              disabled: !(
                selectedUser && onRemoveUser && canRemoveUser && canRemoveUser(selectedUser.userId)
              ) || !canManageMembers,
              onClick: () => {
                if (
                  selectedUser &&
                  onRemoveUser &&
                  canRemoveUser &&
                  canRemoveUser(selectedUser.userId) &&
                  canManageMembers
                ) {
                  onRemoveUser(selectedUser.userId);
                }
                setShowUserMenu(false);
                setSelectedUser(null);
              }
            },
            {
              id: 'roles',
              icon: canManageMembers ? 'ri-user-star-line' : 'ri-lock-line',
              label: 'Настройка ролей',
              disabled: !(selectedUser && selectedUser.onManageRoles) || !canManageMembers,
              onClick: () => {
                if (selectedUser?.onManageRoles) {
                  selectedUser.onManageRoles();
                }
                setShowUserMenu(false);
                setSelectedUser(null);
              }
            },
            {
              id: 'permissions',
              icon: canManagePermissions ? 'ri-settings-3-line' : 'ri-lock-line',
              label: 'Настройка прав',
              disabled: !(selectedUser && selectedUser.onManagePermissions) || !canManagePermissions,
              onClick: () => {
                if (selectedUser?.onManagePermissions) {
                  selectedUser.onManagePermissions();
                }
                setShowUserMenu(false);
                setSelectedUser(null);
              }
            },
            ...(selectedUser?.onConfigureDefaultRate
              ? [
                  {
                    id: 'default-rate',
                    icon: 'ri-money-ruble-circle-line' as const,
                    label: 'Настроить ставку по умолчанию',
                    disabled: false,
                    onClick: () => {
                      selectedUser.onConfigureDefaultRate?.();
                      setShowUserMenu(false);
                      setSelectedUser(null);
                    },
                  },
                ]
              : []),
          ]}
        />
      </BottomSheet>
    </div>
  );
};

export default UserList;
